// ── Theme definitions ────────────────────────────────────────────────

export const DARK_THEME = {
  bg0:     "#0b0e1a",         // page background
  bg1:     "#111520",         // sidebar, panels
  bg2:     "#171c2e",         // cards, sections
  bg3:     "#1e2438",         // elevated, chips
  border1: "rgba(255,255,255,0.07)",
  border2: "rgba(255,255,255,0.13)",

  text1: "#eef0ff",
  text2: "#8891b4",
  text3: "#4c5270",

  indigo:      "#6366f1",
  indigoDim:   "rgba(99,102,241,0.14)",
  indigoGlow:  "rgba(99,102,241,0.30)",
  violet:      "#8b5cf6",
  violetDim:   "rgba(139,92,246,0.14)",
  deep:        "#4f46e5",

  emerald:     "#6366f1",     // kept as brand/indigo alias (legacy compat)
  emeraldDim:  "rgba(99,102,241,0.13)",
  emeraldGlow: "rgba(99,102,241,0.28)",
  online:      "#10b981",    // ← true green for "connected" status
  onlineDim:   "rgba(16,185,129,0.13)",

  sky:         "#38bdf8",  skyDim:   "rgba(56,189,248,0.11)",
  amber:       "#f59e0b",  amberDim: "rgba(245,158,11,0.12)",
  rose:        "#f43f5e",  roseDim:  "rgba(244,63,94,0.11)",
  teal:        "#14b8a6",  tealDim:  "rgba(20,184,166,0.11)",

  shadow:      "0 1px 3px rgba(0,0,0,0.35), 0 4px 12px rgba(0,0,0,0.25)",
  shadowLg:    "0 8px 32px rgba(0,0,0,0.5)",
};

export const LIGHT_THEME = {
  bg0:     "#f4f6fb",         // page background
  bg1:     "#ffffff",         // sidebar, panels
  bg2:     "#edf0fa",         // inputs, chips
  bg3:     "#e3e7f5",         // hover, subtle
  border1: "#e4e8f4",
  border2: "#c8cee4",

  text1: "#0d1020",
  text2: "#525a7a",
  text3: "#8e97ba",

  indigo:      "#5b5ef0",
  indigoDim:   "rgba(91,94,240,0.10)",
  indigoGlow:  "rgba(91,94,240,0.22)",
  violet:      "#7c3aed",
  violetDim:   "rgba(124,58,237,0.10)",
  deep:        "#4338ca",

  emerald:     "#5b5ef0",     // kept as brand/indigo alias (legacy compat)
  emeraldDim:  "rgba(91,94,240,0.10)",
  emeraldGlow: "rgba(91,94,240,0.22)",
  online:      "#059669",    // ← true green for "connected" status
  onlineDim:   "rgba(5,150,105,0.10)",

  sky:         "#0284c7",  skyDim:   "rgba(2,132,199,0.10)",
  amber:       "#d97706",  amberDim: "rgba(217,119,6,0.10)",
  rose:        "#e11d48",  roseDim:  "rgba(225,29,72,0.10)",
  teal:        "#0d9488",  tealDim:  "rgba(13,148,136,0.10)",

  shadow:      "0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.08)",
  shadowLg:    "0 8px 32px rgba(0,0,0,0.12)",
};

// ── Live mutable T object ─────────────────────────────────────────────
const _init = typeof localStorage !== "undefined" && localStorage.getItem("rq_theme") === "light"
  ? LIGHT_THEME : DARK_THEME;

export const T = { ..._init };

// ── Apply theme — mutate T + update ALL CSS vars + body ───────────────
export const applyTheme = (isDark) => {
  const src = isDark ? DARK_THEME : LIGHT_THEME;
  Object.assign(T, src);

  const r = document.documentElement;
  const set = (k, v) => r.style.setProperty(k, v);

  set("--rq-bg0",          src.bg0);
  set("--rq-bg1",          src.bg1);
  set("--rq-bg2",          src.bg2);
  set("--rq-bg3",          src.bg3);
  set("--rq-border1",      src.border1);
  set("--rq-border2",      src.border2);
  set("--rq-text1",        src.text1);
  set("--rq-text2",        src.text2);
  set("--rq-text3",        src.text3);
  set("--rq-indigo",       src.indigo);
  set("--rq-indigo-dim",   src.indigoDim);
  set("--rq-violet",       src.violet);
  set("--rq-emerald",      src.emerald);
  set("--rq-emerald-dim",  src.emeraldDim);
  set("--rq-online",       src.online);
  set("--rq-online-dim",   src.onlineDim);
  set("--rq-sky",          src.sky);
  set("--rq-amber",        src.amber);
  set("--rq-rose",         src.rose);
  set("--rq-shadow",       src.shadow);

  document.body.style.background = src.bg0;
  document.body.style.color      = src.text1;
  localStorage.setItem("rq_theme", isDark ? "dark" : "light");
};

export const base = {
  font: "'Inter', 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  mono: "'JetBrains Mono', 'Fira Code', monospace",
};

// ── Global stylesheet ────────────────────────────────────────────────
export const injectGlobalStyles = () => {
  if (document.getElementById("rq-g")) return;
  const s = document.createElement("style");
  s.id = "rq-g";
  s.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html, body, #root { height: 100%; }

    body {
      background: var(--rq-bg0, ${T.bg0});
      color:      var(--rq-text1, ${T.text1});
      font-family: ${base.font};
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      line-height: 1.5;
      transition: background 0.2s, color 0.18s;
    }

    /* ── Scrollbar ── */
    ::-webkit-scrollbar           { width: 5px; height: 5px; }
    ::-webkit-scrollbar-track     { background: transparent; }
    ::-webkit-scrollbar-thumb     { background: var(--rq-border2, rgba(99,102,241,0.2)); border-radius: 3px; }
    ::-webkit-scrollbar-thumb:hover { background: var(--rq-indigo, #6366f1); opacity: 0.6; }

    /* ── Form elements ── */
    input, textarea, select, button { font-family: ${base.font} !important; }
    input[type="range"]  { accent-color: var(--rq-indigo, #6366f1); }
    input::placeholder   { color: var(--rq-text3, #4c5270); }
    textarea::placeholder { color: var(--rq-text3, #4c5270); }
    select option        { background: var(--rq-bg2, #171c2e); color: var(--rq-text1); }

    /* ── Keyframes ── */
    @keyframes fadeUp       { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
    @keyframes fadeIn       { from{opacity:0} to{opacity:1} }
    @keyframes spin         { to{transform:rotate(360deg)} }
    @keyframes blink        { 0%,100%{opacity:1} 50%{opacity:0.2} }
    @keyframes pulse        { 0%,100%{opacity:1} 50%{opacity:0.4} }
    @keyframes pulseGlow    { 0%,100%{box-shadow:0 0 0 0 rgba(5,150,105,0.5)} 70%{box-shadow:0 0 0 6px rgba(5,150,105,0)} }
    @keyframes slideIn      { from{opacity:0;transform:translateX(-8px)} to{opacity:1;transform:translateX(0)} }
    @keyframes toastIn      { from{opacity:0;transform:translateY(14px) scale(0.96)} to{opacity:1;transform:translateY(0) scale(1)} }
    @keyframes shimmer      { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
    @keyframes typingBounce { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-5px)} }
    @keyframes scaleIn      { from{opacity:0;transform:scale(0.95)} to{opacity:1;transform:scale(1)} }

    /* ── Interactive utilities ── */
    .nav-item {
      transition: background 0.15s, color 0.15s !important;
    }
    .nav-item:hover {
      background: var(--rq-indigo-dim, rgba(99,102,241,0.1)) !important;
      color: var(--rq-indigo, #6366f1) !important;
    }
    .chat-row { transition: background 0.12s; }
    .chat-row:hover { background: var(--rq-bg3, #1e2438) !important; }

    .card-hover { transition: box-shadow 0.18s, transform 0.18s, border-color 0.18s; }
    .card-hover:hover {
      box-shadow: var(--rq-shadow), 0 0 0 1px rgba(99,102,241,0.15) !important;
      transform: translateY(-2px);
      border-color: rgba(99,102,241,0.25) !important;
    }

    .btn-primary:hover  { filter:brightness(1.1); transform:translateY(-1px); box-shadow:0 4px 18px var(--rq-indigo-dim,rgba(99,102,241,0.35)); }
    .btn-danger:hover   { filter:brightness(1.1); transform:translateY(-1px); }
    .btn-ghost:hover    { background: var(--rq-bg3, #1e2438) !important; }
    .btn-secondary:hover{ background: var(--rq-bg3, #1e2438) !important; }
    .btn-outline:hover  { border-color: var(--rq-indigo, #6366f1) !important; color: var(--rq-indigo, #6366f1) !important; }

    .skeleton {
      background: linear-gradient(90deg, var(--rq-bg2) 25%, var(--rq-bg3) 50%, var(--rq-bg2) 75%);
      background-size: 200% 100%;
      animation: shimmer 1.6s infinite;
      border-radius: 6px;
    }

    /* ── Modal animation ── */
    @keyframes fadeSlideIn { from{opacity:0;transform:translateY(12px) scale(0.97)} to{opacity:1;transform:translateY(0) scale(1)} }

    /* ── Connect card ── */
    .connect-card:hover {
      border-color: rgba(99,102,241,0.4) !important;
      background: var(--rq-bg3) !important;
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(0,0,0,0.15);
    }

    /* ── Focus rings ── */
    input:focus-visible,
    textarea:focus-visible,
    select:focus-visible {
      outline: 2px solid var(--rq-indigo, #6366f1);
      outline-offset: 2px;
    }
    button:focus-visible {
      outline: 2px solid var(--rq-indigo, #6366f1);
      outline-offset: 2px;
      border-radius: 8px;
    }

    /* ── Mobile responsive grid helpers ── */
    img, video, canvas, svg { max-width: 100%; }
    table { max-width: 100%; }

    @media (max-width: 1100px) {
      .grid-4 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
      .grid-3 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
    }

    @media (max-width: 768px) {
      .grid-4  { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 10px !important; }
      .grid-3  { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 10px !important; }
      .grid-2  { grid-template-columns: 1fr !important; gap: 12px !important; }
      .grid-2-keep { grid-template-columns: 1fr 1fr !important; gap: 10px !important; }
      .mobile-hide { display: none !important; }
      .mobile-stack { flex-direction: column !important; align-items: flex-start !important; }
      .mobile-full  { width: 100% !important; max-width: 100% !important; }
      .mobile-scroll-x { overflow-x: auto !important; }
      [role="dialog"] { width: calc(100vw - 24px) !important; max-width: calc(100vw - 24px) !important; max-height: calc(100dvh - 24px) !important; overflow-y: auto !important; }
    }
    @media (max-width: 480px) {
      .grid-4  { grid-template-columns: 1fr !important; gap: 8px !important; }
      .grid-3  { grid-template-columns: 1fr !important; gap: 8px !important; }
      .grid-2  { grid-template-columns: 1fr !important; }
      .grid-2-keep { grid-template-columns: 1fr !important; }
    }
  `;
  document.head.appendChild(s);
  const isDark = T.bg0 === DARK_THEME.bg0;
  applyTheme(isDark);
};
